import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import "../../i18n";
import { Notice } from "../../design/components";
import { ApiError } from "../../api/client";
import { useAddUser, type AddUserResponse, type RoleInfo, type UserRow, type UsersSource } from "../../api/users";
import type { Role } from "../../api/types";
import { useFocusTrap } from "../../lib/useFocusTrap";
import { CopyButton } from "../companies/CopyButton";
import { RoleSelect } from "./RoleSelect";
import styles from "./users.module.css";

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* Kept in English only: userFailureMessage() is a plain function (not a component) shared with
   UsersTable.tsx and asserted on verbatim by InviteDialog.test.tsx, so it cannot take a `t`
   function without breaking that test's call signature. It is the last-resort fallback, used
   only when the server's own error has no message of its own. */
export const USER_MESSAGE_BY_CODE: Record<string, string> = {
  already_member: "This person already has access to this company",
  cannot_modify_self: "You cannot change your own access.",
  last_entity_admin: "This is the last administrator for this company",
  role_not_invitable: "That role cannot be invited.",
  role_not_assignable: "That role cannot be given here.",
};

/** The server's wording wins; the local copy only covers a body without one. */
export function userFailureMessage(error: unknown, fallback: string): string {
  if (!(error instanceof ApiError)) return fallback;
  return error.message || USER_MESSAGE_BY_CODE[error.code] || fallback;
}

/** The one-time invitation link; the list never carries it, so it is shown here exactly once. */
export function InviteLink({ url }: { url: string }) {
  const { t } = useTranslation();
  return (
    <div className={styles.linkPanel}>
      <div className={styles.linkRow}>
        <code className={styles.link} data-testid="invite-link">
          {url}
        </code>
        <CopyButton value={url} label={t("users.invite.copyLink", "Copy invitation link")} />
      </div>
      <p className={styles.linkNote}>
        {t(
          "users.invite.linkNote",
          "Send this link to them. They set their own password and second factor; nobody else can.",
        )}
      </p>
    </div>
  );
}

export function InviteDialog({
  entityId,
  source = "console",
  roles,
  onClose,
  resend,
}: {
  entityId?: string | undefined;
  source?: UsersSource | undefined;
  roles: RoleInfo[];
  onClose: () => void;
  /** Re-posts an open invitation, which revokes and reissues it. */
  resend?: UserRow | undefined;
}) {
  const { t } = useTranslation();
  const invitable = roles.filter((role) => role.invitable);
  const [email, setEmail] = useState(resend?.email ?? "");
  const [role, setRole] = useState<Role>(resend?.role ?? invitable[0]?.key ?? "readonly");
  const [result, setResult] = useState<AddUserResponse | null>(null);
  const add = useAddUser({ source, entityId });
  const box = useFocusTrap<HTMLDivElement>(onClose);

  const title = resend ? t("users.invite.resendTitle", "Resend invitation") : t("users.invite.inviteTitle", "Invite a person");
  const valid = EMAIL_SHAPE.test(email.trim()) && invitable.length > 0;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!valid || add.isPending) return;
    add.mutate({ email: email.trim(), role }, { onSuccess: setResult });
  }

  return (
    <div className={styles.dialogWrap}>
      <div className={styles.dialog} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={box}>
        <h2 className={styles.dialogTitle}>{title}</h2>
        {result ? (
          <>
            {result.kind === "invitation" ? (
              <InviteLink url={result.inviteUrl} />
            ) : (
              <Notice kind="ok">
                {t(
                  "users.invite.alreadyHadAccount",
                  "This person already had an account and now has access to this company.",
                )}
              </Notice>
            )}
            <div className={styles.dialogActions}>
              <button type="button" className="btn btn-sm btn-primary" onClick={onClose}>
                {t("users.invite.done", "Done")}
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={submit}>
            <label className={styles.dialogLabel} htmlFor="invite-email">
              {t("users.invite.email", "Email")}
            </label>
            <input
              id="invite-email"
              className={`input ${styles.wide}`}
              type="email"
              autoComplete="off"
              value={email}
              readOnly={Boolean(resend)}
              onChange={(event) => setEmail(event.target.value)}
            />
            <label className={styles.dialogLabel} htmlFor="invite-role">
              {t("users.invite.role", "Role")}
            </label>
            <RoleSelect id="invite-role" value={role} roles={invitable} disabled={Boolean(resend)} onChange={setRole} />
            {add.isError ? (
              <div className={styles.dialogNotice}>
                <Notice kind="fail">
                  {userFailureMessage(add.error, t("users.invite.sendError", "The invitation could not be sent."))}
                </Notice>
              </div>
            ) : null}
            <div className={styles.dialogActions}>
              <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}>
                {t("users.invite.cancel", "Cancel")}
              </button>
              <button type="submit" className="btn btn-sm btn-primary" disabled={!valid || add.isPending}>
                {add.isPending
                  ? t("users.invite.sending", "Sending…")
                  : resend
                    ? t("users.invite.resendTitle", "Resend invitation")
                    : t("users.invite.send", "Send invitation")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
